@if (initialized) {
    <div class="cn-abgleich-comparison">

        <!-- ===================== EMPTY (keine Auswahl) ===================== -->
        @if (state === 'empty') {
            <div class="surface-0 border-1 surface-border border-round p-5 text-center">
                <i class="fa-regular fa-scale-balanced text-3xl text-600 mb-3 block"></i>
                <div class="text-lg font-medium mb-2">Kein Modell-Abgleich ausgewählt</div>
                <div class="text-600 mb-3">
                    Bitte den Modell-Abgleich auf der Abgleich-Seite starten.
                </div>
                <p-button label="Zur Abgleich-Seite"
                          icon="fa-regular fa-arrow-right"
                          [outlined]="true"
                          (onClick)="goToReconciliation()">
                </p-button>
            </div>
        }

        <!-- ===================== LOADING ===================== -->
        @if (state === 'loading') {
            <div class="surface-0 border-1 surface-border border-round p-3">
                <div class="flex align-items-center gap-2 text-600 mb-2">
                    <i class="fa-regular fa-spinner fa-spin"></i>
                    <span>Modell-Abgleich wird geladen …</span>
                </div>
                <p-progressBar mode="indeterminate" styleClass="cn-abgleich-progress"></p-progressBar>
            </div>
        }

        <!-- ===================== ERROR ===================== -->
        @if (state === 'error') {
            <p-message severity="error" class="block mb-3" [text]="errorMessage"></p-message>
            <p-button label="Neu laden"
                      icon="fa-regular fa-rotate-right"
                      [outlined]="true"
                      (onClick)="reload()">
            </p-button>
        }

        <!-- ===================== READY ===================== -->
        @if (state === 'ready' && result) {

            <!-- Aktionsleiste -->
            <div class="flex justify-content-end mb-3">
                <p-button label="Neu laden"
                          icon="fa-regular fa-rotate-right"
                          [outlined]="true"
                          size="small"
                          (onClick)="reload()">
                </p-button>
            </div>

            <!-- ===================== VERDICT-BANNER ===================== -->
            @if (result.verdict; as verdict) {
                <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
                    <div class="grid">
                        <div class="col-12 md:col-6 flex align-items-start gap-2">
                            <i class="fa-regular fa-trophy text-yellow-600 mt-1"></i>
                            <div>
                                <div class="text-sm text-600">Am lukrativsten</div>
                                <div class="text-lg font-medium">
                                    {{ verdict.mostLucrativeModelName ?? ('Modell #' + verdict.mostLucrativeModelId) }}
                                    <span class="font-semibold"
                                          [class.text-green-600]="verdict.upliftVsActive >= 0"
                                          [class.text-red-600]="verdict.upliftVsActive < 0">
                                        ({{ verdict.upliftVsActive >= 0 ? '+' : '' }}{{ verdict.upliftVsActive | currency:'EUR':'symbol':'1.2-2':'de' }})
                                    </span>
                                    <span class="text-600">ggü. Aktiv-Modell</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 md:col-6 flex align-items-start gap-2">
                            <i class="fa-regular fa-bullseye text-primary mt-1"></i>
                            <div>
                                <div class="text-sm text-600">Beste Übereinstimmung mit Ist</div>
                                <div class="text-lg font-medium">
                                    {{ verdict.bestMatchToIstModelName ?? ('Modell #' + verdict.bestMatchToIstModelId) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }

            <!-- ===================== SUMMEN-KACHELN ===================== -->
            <div class="grid mb-3">
                <!-- Ist -->
                <div class="col-12 md:col-6 lg:col-3">
                    <div class="surface-0 border-1 surface-border border-round p-3 h-full">
                        <div class="text-sm text-600 mb-2">Ist (empfangen, gesamt)</div>
                        <div class="text-2xl font-bold">
                            {{ result.receivedTotal | currency:'EUR':'symbol':'1.2-2':'de' }}
                        </div>
                    </div>
                </div>

                <!-- Je Modell -->
                @for (model of result.perModel; track model.modelId) {
                    <div class="col-12 md:col-6 lg:col-3">
                        <div class="surface-0 border-1 border-round p-3 h-full"
                             [class.surface-border]="!isMostLucrative(model.modelId)"
                             [class.cn-card-best]="isMostLucrative(model.modelId)">
                            <div class="flex align-items-center justify-content-between mb-2 gap-2">
                                <span class="text-sm font-medium">{{ modelName(model) }}</span>
                                <span class="flex align-items-center gap-1">
                                    @if (model.active) {
                                        <p-tag severity="success" value="Aktiv"></p-tag>
                                    }
                                    @if (isMostLucrative(model.modelId)) {
                                        <i class="fa-regular fa-trophy text-yellow-600"
                                           pTooltip="Am lukrativsten" tooltipPosition="top"></i>
                                    }
                                </span>
                            </div>
                            <div class="text-xl font-bold mb-1">
                                {{ model.expectedTotal | currency:'EUR':'symbol':'1.2-2':'de' }}
                            </div>
                            <div class="text-sm"
                                 [class.text-green-600]="model.deltaReceivedMinusExpected >= 0"
                                 [class.text-red-600]="model.deltaReceivedMinusExpected < 0">
                                Δ vs. Ist:
                                {{ model.deltaReceivedMinusExpected >= 0 ? '+' : '' }}{{ model.deltaReceivedMinusExpected | currency:'EUR':'symbol':'1.2-2':'de' }}
                            </div>
                        </div>
                    </div>
                }
            </div>

            <!-- ===================== SUB-TABS: JE VERTRAG / VERLAUF ===================== -->
            <div class="surface-0 border-1 surface-border border-round p-3">
                <p-tabs [value]="activeSubTab">
                    <p-tablist>
                        <p-tab [value]="0">Je Vertrag</p-tab>
                        <p-tab [value]="1">Verlauf</p-tab>
                    </p-tablist>
                    <p-tabpanels>

                        <!-- ----- Je Vertrag ----- -->
                        <p-tabpanel [value]="0">
                            @if (result.perContract?.length) {
                                <p-table [value]="result.perContract"
                                         styleClass="p-datatable-sm"
                                         [scrollable]="true">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            <th>Vertrag</th>
                                            <th class="text-right">Ist</th>
                                            @for (model of result.perModel; track model.modelId) {
                                                <th class="text-right">{{ modelName(model) }} Soll</th>
                                                <th class="text-right">{{ modelName(model) }} Δ</th>
                                            }
                                            <th>Gewinner</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-contract>
                                        <tr>
                                            <td class="font-medium">{{ contractLabel(contract) }}</td>
                                            <td class="text-right">
                                                {{ contract.receivedTotal | currency:'EUR':'symbol':'1.2-2':'de' }}
                                            </td>
                                            @for (model of result.perModel; track model.modelId) {
                                                <td class="text-right">
                                                    {{ (cellExpected(contract, model.modelId)) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                                </td>
                                                <td class="text-right"
                                                    [class.text-green-600]="cellDelta(contract, model.modelId) >= 0"
                                                    [class.text-red-600]="cellDelta(contract, model.modelId) < 0">
                                                    {{ (cellDelta(contract, model.modelId)) | currency:'EUR':'symbol':'1.2-2':'de' }}
                                                </td>
                                            }
                                            <td>
                                                <p-tag severity="success"
                                                       [value]="contractWinnerName(contract)"></p-tag>
                                            </td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            } @else {
                                <div class="text-center text-600 p-4">
                                    Keine Vertragsdaten im Abgleich-Ergebnis.
                                </div>
                            }
                        </p-tabpanel>

                        <!-- ----- Verlauf ----- -->
                        <p-tabpanel [value]="1">
                            <div class="flex align-items-start gap-2 text-xs text-500 mb-3">
                                <i class="fa-regular fa-circle-info mt-1"></i>
                                <span>Modellwerte sind Gesamterwartung (Referenz), keine Monatsprojektion.</span>
                            </div>
                            @if (chartData) {
                                <div class="cn-chart-host">
                                    <p-chart type="bar" [data]="chartData" [options]="chartOptions"
                                             height="100%"></p-chart>
                                </div>
                            } @else {
                                <div class="text-center text-600 p-4">
                                    Kein Monatsverlauf im Abgleich-Ergebnis.
                                </div>
                            }
                        </p-tabpanel>

                    </p-tabpanels>
                </p-tabs>
            </div>

        }

    </div>
}
